import * as THREE from 'three';
import { INTERACTIVE_USERDATA_KEY } from '../config/constants';

export type PickInfo = {
  /** Canvas-space coordinates on the picked panel. */
  canvasX: number;
  canvasY: number;
  /** Width/height of the source panel canvas. */
  canvasW: number;
  canvasH: number;
};

export type PickHandler = (info: PickInfo) => void;

/** Marks a mesh as interactive. Stores handler + canvas dimensions for UV→px conversion. */
export function markInteractive(
  mesh: THREE.Mesh,
  canvasW: number,
  canvasH: number,
  handler: PickHandler,
): void {
  mesh.userData[INTERACTIVE_USERDATA_KEY] = handler;
  mesh.userData['canvasW'] = canvasW;
  mesh.userData['canvasH'] = canvasH;
}

export function unmarkInteractive(mesh: THREE.Mesh): void {
  delete mesh.userData[INTERACTIVE_USERDATA_KEY];
}

const raycaster = new THREE.Raycaster();
const tempOrigin = new THREE.Vector3();
const tempDir = new THREE.Vector3(0, 0, -1);

/**
 * Cast a ray from controller forward (-Z local) and call the handler of the first
 * interactive mesh hit. UV is converted to canvas px stored in userData.
 */
export function castFromController(controller: THREE.Object3D, scene: THREE.Object3D): boolean {
  controller.updateMatrixWorld(true);
  tempOrigin.setFromMatrixPosition(controller.matrixWorld);
  tempDir.set(0, 0, -1).transformDirection(controller.matrixWorld).normalize();
  raycaster.set(tempOrigin, tempDir);

  const intersects = raycaster.intersectObject(scene, true);
  for (const hit of intersects) {
    const handler = hit.object.userData[INTERACTIVE_USERDATA_KEY] as PickHandler | undefined;
    if (!handler) continue;
    if (!hit.uv) continue;
    const w = (hit.object.userData['canvasW'] as number | undefined) ?? 0;
    const h = (hit.object.userData['canvasH'] as number | undefined) ?? 0;
    handler({
      canvasX: hit.uv.x * w,
      canvasY: (1 - hit.uv.y) * h,
      canvasW: w,
      canvasH: h,
    });
    return true;
  }
  return false;
}
